import {Cache} from 'pixi.js';

// Match the adult character and vehicle prefabs. Coordinates in pose packets
// are physics centers; these are the textures' authored offsets from them.
export const characterOffsets = {
	body: [4.1688, 1.3835, 0], head: [2.9897, 0.7854, 0],
	shoulderLeft: [0.5731, -0.1606, 0], shoulderRight: [0.5731, -0.1606, 0],
	armLeft: [0.2976, -0.8938, 0], armRight: [0.2976, -0.8938, 0],
	handLeft: [1.7975, 2.9104, 0], handRight: [1.7975, 2.9104, 0],
	belly: [8.7468, 1.1579, -0.2],
	thighLeft: [0.4132, -2.3665, 0], thighRight: [0.4132, -2.3665, 0],
	legLeft: [0.1988, -3.1416, 0], legRight: [0.1988, -3.1416, 0],
	feetLeft: [1.5151, 1.2143, 0], feetRight: [1.5151, 1.2143, 0],
};

export const vehicleOffsets = {
	1: {body: [20.025, 1.5208, 0], wheelBack: [0.792, 0.7446, 0], wheelFront: [0.0055, -3.1416, 0], pedals: [0.3605, 0.1433, 0]},
	2: {body: [17.5997, 2.6234, 0], wheelBack: [0, 0, 0], wheelFront: [0, -1.5708, 0], frontSuspension: [0.4698, 2.1467, 0], backSuspension: [0.3446, -1.0035, 0]},
	4: {body: [4.17, -1.546, 0], wheelBack: [0.435, -0.384, 0], wheelFront: [0.435, -0.384, 0]},
	6: {body: [3.0245, 1.3957, 0], hammer: [61.8921, -3.1255, 0]},
};

const leftLeg = ['thighLeft', 'legLeft', 'feetLeft'];
const leftArm = ['shoulderLeft', 'armLeft', 'handLeft'];
const core = ['belly', 'body', 'head'];
const rightLeg = ['thighRight', 'legRight', 'feetRight'];
const rightArm = ['shoulderRight', 'armRight', 'handRight'];
const front = [...core, ...rightLeg, ...rightArm];
const unmounted = [...leftLeg, ...leftArm, ...front];

export const renderOrders = {
	1: [...leftArm, ...leftLeg, 'vehicle.pedals', 'vehicle.wheelFront', 'vehicle.wheelBack', 'vehicle.body', ...front],
	2: ['thighLeft', 'legLeft', 'vehicle.wheelBack', 'vehicle.wheelFront', 'vehicle.frontSuspension', 'vehicle.backSuspension', ...leftArm, 'feetLeft', 'vehicle.body', ...front],
	3: unmounted,
	4: ['vehicle.body', 'vehicle.wheelFront', 'vehicle.wheelBack', ...unmounted],
	5: [...leftArm, ...leftLeg, 'vehicle.ball', ...front],
	6: [...leftLeg, ...leftArm, ...core, ...rightLeg, 'vehicle.body', 'vehicle.hammer', ...rightArm],
};

export const applyRenderOrder = (container, character, vehicle, type) => {
	let index = 0;
	for (const name of renderOrders[type] || unmounted) {
		const sprite = name.startsWith('vehicle.') ? vehicle?.[name.slice(8)] : character?.[name];
		if (sprite?.parent === container) {
			if (sprite.flesh?.parent === container) container.setChildIndex(sprite.flesh, index++);
			container.setChildIndex(sprite, index++);
		}
	}
};

export const applyRenderPose = (sprite, state, offset = [0, 0, 0], origin = null) => {
	const [length, offsetAngle, rotationOffset] = offset;
	const angle = state.r * Math.PI / 180;
	const x = length * Math.cos(offsetAngle) * (state.mirror ? -1 : 1);
	const y = -length * Math.sin(offsetAngle);
	sprite.x = state.x - (origin?.x || 0) + x * Math.cos(angle) - y * Math.sin(angle);
	sprite.y = state.y - (origin?.y || 0) + x * Math.sin(angle) + y * Math.cos(angle);
	sprite.rotation = angle - rotationOffset;
	sprite.scale.x = state.mirror ? -1 : 1;
	if (sprite.flesh) {
		sprite.flesh.position.copyFrom(sprite.position);
		sprite.flesh.rotation = sprite.rotation;
		sprite.flesh.scale.copyFrom(sprite.scale);
		sprite.flesh.visible = sprite.visible && sprite.rippleDamageVisible !== false;
	}
};

export const getHatOffset = (length, angle) => {
	// The hat is a child of the rendered head, not its physics center. Both
	// offsets use the same local convention and inherit the head's flip/angle.
	const [headLength, headAngle] = characterOffsets.head;
	return {x: length * Math.cos(angle) - headLength * Math.cos(headAngle),
		y: -length * Math.sin(angle) + headLength * Math.sin(headAngle)};
};

export const setSpriteAnchor = (sprite, textureName = `${sprite.texture.label}0000`) => {
	// The transmitted atlas reserves padded slots. Its slot center can differ
	// from the source texture center used by the local character's pivot.
	const source = Cache.has(textureName) ? Cache.get(textureName) : null;
	sprite.anchor.set(source ? source.width / sprite.texture.width / 2 : 0.5,
		source ? source.height / sprite.texture.height / 2 : 0.5);
};
